/**
 * The strip map's PNG export (strip-map spec §3.5): the strip, its axes and
 * its legend, composed offscreen at 2x device pixels and handed back as a
 * `Blob`.
 *
 * Pure of React and of hooks, deliberately: the strip tab's own export
 * button and the dock's Export menu (Task 4) both call `stripPngBlob`, and
 * the Export menu has to produce the identical file without the strip tab
 * ever having been opened. A function of the data alone gives both callers
 * one picture; a ref pulled out of `StripMapCanvas` would give them two,
 * which is exactly how the two panes could drift apart.
 *
 * The picture is a small, deliberate SUBSET of the live tab. No hover
 * crosshair, no brush wash, no walked-station rule — none of those exist
 * without a pointer or a live selection, so a static export has nothing to
 * draw them from. The per-bin verdict row is a different kind of omission:
 * `b.verdict` is ordinary static data, present in a still export exactly as
 * much as it is in the live tab, but it is left out here as a deliberate
 * print-scope choice for this task rather than for lack of a pointer — a
 * later task can add it to this module's picture without changing the
 * contract. What a printed page keeps is the strip image, the chainage axis
 * with its station equations, the offset gutter, the swath rules, the
 * low-coverage hatch and the legend, which is what spec §3.5 asks for.
 *
 * Printed inks are fixed hex literals — the light `SV_COLORS` steps
 * (`tokens.ts`) restated, not imported with the rest of the palette module —
 * because a PNG has no dark mode to answer to, exactly as
 * `processReport/palette.ts` states for the PDF's own light-only palette.
 */
import { equationMarkers, thinLabels } from './chartLayout';
import { formatFixed, staOnly } from './format';
import {
  buildStripLut,
  buildStripPixels,
  lowCoverageRuns,
  markSpanPx,
  STA_LABEL_MIN_GAP_PX,
  STRIP_MARGIN,
  xForStation,
  yForOffsetM,
  type StripInks,
  type StripMatrix,
} from './stripModel';
import type { StationVolumesAnalysisKind, StationVolumesJson } from './types';

/** Rows of chrome the export adds above and below the strip, in CSS px. */
const TITLE_ROW_PX = 22;
/** Floor for the legend row — grown per `legendLines.length` below when the
 *  wrapped text needs more (fix round 1: German/Hungarian run ~40% longer
 *  than English and must never lose a line to truncation). */
const LEGEND_ROW_PX = 30;
const LEGEND_LINE_HEIGHT_PX = 11;
const LEGEND_TOP_PAD_PX = 12;
const LEGEND_BOTTOM_PAD_PX = 6;
/** Device-pixel multiplier (spec §3.5: "2x device pixels"). */
export const STRIP_PNG_SCALE = 2;

/** The light `SV_COLORS` steps (`tokens.ts`), restated as literals: see the
 *  module comment for why this file does not import the palette. */
const SURFACE = '#fcfcfb';
const INK = '#0b0b0a';
const MUTED = '#63625d';
const HAUL = '#52514e';
const CLEAR = '#8f8e85';
const CAUTION = '#f2b705';
const PRINT_INKS: StripInks = { cut: '#e34948', fill: '#2a78d6', neutral: '#f0efec' };

const AXIS_FONT = '9px system-ui, sans-serif';
const TITLE_FONT = 'bold 13px system-ui, sans-serif';
const LEGEND_FONT = '9px system-ui, sans-serif';

export interface StripPngInput {
  json: StationVolumesJson;
  matrix: StripMatrix;
  /** Half-width of the neutral band, in metres (spec §3.2). */
  toleranceM: number;
  /** Half-width of the diverging ramp, in metres (spec §3.3). */
  spanM: number;
  offsetStartM: number;
  offsetStepM: number;
  /**
   * Carried for input-shape parity with `StripMapCanvasProps` — the caller
   * builds both from the same run. Not read here: the print ramp's polarity
   * is baked into `PRINT_INKS` via `stripColorAt`'s own `'surfaceDifference'`
   * call (`stripModel.ts`), the same call for the same reason on every
   * signed surface this product draws.
   */
  kind: StationVolumesAnalysisKind;
  /**
   * Folded into the printed `axisLeft` / `axisRight` labels below, the same
   * way `StripMapCanvas.tsx` draws `` `${t('axisLeft')} ${formatFixed(
   * swathLeftM, 1)}` ``. The caller passes the bare word (its locale keys
   * are plain `"L"` / `"R"`); this module appends the figure itself, so a
   * live tab reading "L 1.5" and an exported PNG reading "L" — the two
   * pictures diverging — cannot happen by a caller simply forgetting to
   * fold the width in.
   */
  swathLeftM: number;
  swathRightM: number;
  /** The reference's own extent (A17), same units and sign as the offset
   *  axis. Absent draws no rule — most runs have no reference-extent figure
   *  older than the field, or measured nothing. */
  referenceExtentLeftM?: number | null;
  referenceExtentRightM?: number | null;
  /** The strip pane's own box, in CSS px — matches the `width`/`height`
   *  `StripMapCanvas` is given. Title and legend rows are added on top. */
  paneWidth: number;
  paneHeight: number;
  title: string;
  legendText: string;
  axisLeft: string;
  axisCentre: string;
  axisRight: string;
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * The strip, its axes and its legend, painted onto a fresh offscreen
 * `<canvas>` at `STRIP_PNG_SCALE` device pixels. `doc` is injectable only for
 * tests — every real caller uses the default.
 */
export function composeStripPng(input: StripPngInput, doc: Document = document): HTMLCanvasElement {
  const { json, matrix } = input;
  const w = input.paneWidth;

  // The legend row is sized from the ACTUAL wrapped text, measured before
  // the target canvas exists, so a long translation grows the card instead
  // of losing a line off the bottom.
  const legendMaxWidth = Math.max(1, w - STRIP_MARGIN.left - STRIP_MARGIN.right);
  const measureCtx = doc.createElement('canvas').getContext('2d');
  if (!measureCtx) throw new Error('stripPng: no 2D context');
  measureCtx.font = LEGEND_FONT;
  const legendLines = wrapLines(measureCtx, input.legendText, legendMaxWidth);
  const legendRowPx = Math.max(
    LEGEND_ROW_PX,
    LEGEND_TOP_PAD_PX + legendLines.length * LEGEND_LINE_HEIGHT_PX + LEGEND_BOTTOM_PAD_PX,
  );
  const h = input.paneHeight + TITLE_ROW_PX + legendRowPx;

  const cv = doc.createElement('canvas');
  cv.width = w * STRIP_PNG_SCALE;
  cv.height = h * STRIP_PNG_SCALE;
  const ctx = cv.getContext('2d');
  if (!ctx) throw new Error('stripPng: no 2D context');
  ctx.setTransform(STRIP_PNG_SCALE, 0, 0, STRIP_PNG_SCALE, 0, 0);

  // The card. A printed page has no dark mode (processReport/palette.ts).
  ctx.fillStyle = SURFACE;
  ctx.fillRect(0, 0, w, h);

  // ---------------------------------------------------------------- title
  ctx.fillStyle = INK;
  ctx.font = TITLE_FONT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(input.title, STRIP_MARGIN.left, TITLE_ROW_PX - 6);

  // ----------------------------------------------------------- plot geometry
  const plotLeft = STRIP_MARGIN.left;
  const plotTop = TITLE_ROW_PX + STRIP_MARGIN.top;
  const plotWidth = Math.max(1, w - STRIP_MARGIN.left - STRIP_MARGIN.right);
  const plotHeight = Math.max(1, input.paneHeight - STRIP_MARGIN.top - STRIP_MARGIN.bottom);
  const nBins = json.bins.length;
  const colW = plotWidth / Math.max(1, nBins);
  const axisY = plotTop + plotHeight;

  // -------------------------------------------------------- the strip image
  //
  // Same offscreen path the live tab uses: a `columns x nOffsets` RGBA image
  // rasterised once by `buildStripPixels`, blitted with
  // `imageSmoothingEnabled = false` so up- or down-sampling never blurs an
  // exceedance away.
  const lut = buildStripLut(input.toleranceM, input.spanM, PRINT_INKS);
  const columns = Math.min(matrix.nStations, Math.max(1, Math.round(plotWidth))) || 1;
  const img = buildStripPixels(matrix, { columns, lut, spanM: input.spanM });
  const off = doc.createElement('canvas');
  off.width = img.width;
  off.height = img.height;
  const octx = off.getContext('2d');
  if (!octx) throw new Error('stripPng: no 2D context');
  const id = octx.createImageData(img.width, img.height);
  id.data.set(img.data);
  octx.putImageData(id, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(off, plotLeft, plotTop, plotWidth, plotHeight);

  // --------------------------------------------------- low-coverage hatch
  //
  // The same 45° hatch the live tab draws with an SVG `<pattern>` — here
  // built on a 6 x 6 offscreen tile and turned into a `CanvasPattern`.
  // jsdom's `createPattern` always returns `null`, so a solid 35 %-alpha
  // fallback keeps the export meaningful under test and under any browser
  // that (for whatever reason) refuses the pattern.
  //
  // One patch per RUN of adjacent low bins, never thinner than `MIN_MARK_PX`
  // (fix wave): `MAX_BINS` is 20 000, so `fillRect(…, colW, …)` per bin is
  // 0.06 px on this card — a mark that silently vanishes on exactly the long
  // runs that need it, while the legend below still reads "hatched: low
  // coverage". Same rule as the live tab and as `station_strip.py`.
  const lowCoverage = lowCoverageRuns(json.bins.map((b) => !!b.lowCoverage));
  if (lowCoverage.length > 0) {
    const tile = doc.createElement('canvas');
    tile.width = 6;
    tile.height = 6;
    const tctx = tile.getContext('2d');
    let pattern: CanvasPattern | null = null;
    if (tctx) {
      tctx.strokeStyle = HAUL;
      tctx.globalAlpha = 0.75;
      tctx.lineWidth = 1.5;
      tctx.beginPath();
      tctx.moveTo(0, 6);
      tctx.lineTo(6, 0);
      tctx.stroke();
      pattern = ctx.createPattern(tile, 'repeat');
    }
    ctx.save();
    if (pattern) {
      ctx.fillStyle = pattern;
    } else {
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = HAUL;
    }
    for (const [from, to] of lowCoverage) {
      const span = markSpanPx(from, to, nBins, plotLeft, plotWidth);
      ctx.fillRect(span.x, plotTop, span.width, plotHeight);
    }
    ctx.restore();
  }

  // -------------------------------------------------------- the swath rules
  //
  // The strip's own top and bottom edges, exactly as the live tab draws
  // them — not computed from `swathLeftM`/`swathRightM` (those are the
  // gutter's own labels; see the interface comment above).
  ctx.save();
  ctx.strokeStyle = CLEAR;
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.7;
  ctx.beginPath();
  ctx.moveTo(plotLeft, plotTop);
  ctx.lineTo(plotLeft + plotWidth, plotTop);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(plotLeft, axisY);
  ctx.lineTo(plotLeft + plotWidth, axisY);
  ctx.stroke();
  ctx.restore();

  // ------------------------------------------------ the reference extent
  //
  // Dashed, because it is what the coverage figures are a share OF, not a
  // limit anyone surveyed to (A17). Absent fields draw nothing.
  for (const m of [input.referenceExtentLeftM, input.referenceExtentRightM]) {
    if (typeof m !== 'number' || !Number.isFinite(m)) continue;
    const y = yForOffsetM(m, input.offsetStartM, input.offsetStepM, matrix.nOffsets, plotTop, plotHeight);
    ctx.save();
    ctx.strokeStyle = CLEAR;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(plotLeft, y);
    ctx.lineTo(plotLeft + plotWidth, y);
    ctx.stroke();
    ctx.restore();
  }

  // ------------------------------------------------------- chainage axis
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.font = AXIS_FONT;
  const staXs = json.bins.map((_, i) => plotLeft + i * colW);
  const staKeep = thinLabels(staXs, STA_LABEL_MIN_GAP_PX);
  json.bins.forEach((b, i) => {
    if (!staKeep[i]) return;
    const x = plotLeft + i * colW;
    ctx.save();
    ctx.strokeStyle = HAUL;
    ctx.globalAlpha = 0.6;
    ctx.beginPath();
    ctx.moveTo(x, axisY);
    ctx.lineTo(x, axisY + 3);
    ctx.stroke();
    ctx.restore();
    ctx.fillStyle = HAUL;
    ctx.fillText(staOnly(b.staStartDisplayM, 0), x, axisY + 12);
  });

  // Station equations: the chainage jumps, tagged `from → to`.
  const staX = (internal: number) => xForStation(internal, json.params, plotLeft, plotWidth);
  const equations = equationMarkers(json);
  const eqXs = equations.map((e) => staX(e.station));
  const eqKeep = thinLabels(eqXs, STA_LABEL_MIN_GAP_PX);
  equations.forEach((e, k) => {
    const x = staX(e.station);
    ctx.save();
    ctx.strokeStyle = CAUTION;
    ctx.setLineDash([2, 2]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, plotTop);
    ctx.lineTo(x, axisY + 3);
    ctx.stroke();
    ctx.restore();
    if (eqKeep[k]) {
      ctx.fillStyle = CAUTION;
      ctx.fillText(`${staOnly(e.from, 0)} → ${staOnly(e.to, 0)}`, x, axisY + 22);
    }
  });

  // --------------------------------------------------------- offset gutter
  //
  // Left of the alignment is the TOP (spec §3.1). `axisCentre` is drawn
  // verbatim; `axisLeft`/`axisRight` have the swath width FOLDED IN here —
  // see the interface comment on `swathLeftM`/`swathRightM` for why this
  // module does the folding rather than trusting every caller to.
  ctx.fillStyle = HAUL;
  ctx.font = AXIS_FONT;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(`${input.axisLeft} ${formatFixed(input.swathLeftM, 1)}`, plotLeft - 6, plotTop + 9);
  // CL sits where the offset axis CROSSES ZERO, not at the picture's middle:
  // an asymmetric swath (left 3 m, right 8 m) has its centreline off centre,
  // and a middle-anchored "CL" would label offset +2.5 m as the alignment.
  // Clamped into the box, like the Python twin (`station_strip.py`), for the
  // odd swath that does not contain zero at all.
  const centreY = Math.min(
    plotTop + plotHeight,
    Math.max(plotTop, yForOffsetM(0, input.offsetStartM, input.offsetStepM, matrix.nOffsets, plotTop, plotHeight)),
  );
  ctx.fillText(input.axisCentre, plotLeft - 6, centreY + 3);
  ctx.fillText(`${input.axisRight} ${formatFixed(input.swathRightM, 1)}`, plotLeft - 6, plotTop + plotHeight - 2);

  // -------------------------------------------------------------- legend
  //
  // Every wrapped line is drawn — never truncated (see `legendRowPx` above,
  // sized from this same `legendLines`).
  ctx.fillStyle = MUTED;
  ctx.font = LEGEND_FONT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const legendBaseY = TITLE_ROW_PX + input.paneHeight + LEGEND_TOP_PAD_PX;
  legendLines.forEach((line, i) => ctx.fillText(line, STRIP_MARGIN.left, legendBaseY + i * LEGEND_LINE_HEIGHT_PX));

  return cv;
}

/**
 * `composeStripPng` encoded to a PNG `Blob`. Rejects — never resolves
 * `null` — when the browser refuses the encode, so a caller's `await` fails
 * loudly instead of silently downloading nothing.
 */
export function stripPngBlob(input: StripPngInput, doc: Document = document): Promise<Blob> {
  const cv = composeStripPng(input, doc);
  return new Promise<Blob>((resolve, reject) => {
    cv.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('stripPng: the browser could not encode the canvas'));
    }, 'image/png');
  });
}

/**
 * The download filename for a strip PNG: the layer name slugged, the same
 * rule `processSurfaceDifference/index.ts`'s `slugify` uses, so a downloaded
 * file and a registered byproduct are named alike. Falls back to `layer`
 * when the name has no alphanumeric characters at all.
 */
export function stripPngFilename(layerName: string): string {
  return `${(layerName.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'layer').slice(0, 60)}_strip.png`;
}

/**
 * Everything the strip PNG needs that is not already in the run's own
 * documents: the two ramp settings the reader chose, the pane box to draw
 * into, and a `t`.
 *
 * `t` is passed in rather than imported: this module is pure of React, and
 * the two callers (the Strip tab's own button and the dock's Export menu)
 * already hold the hook's `t`.
 */
export interface StripPngSource {
  json: StationVolumesJson;
  /** The decoded `station_offsets.f32` planes — `useStationOffsets`'s shape. */
  offsets: {
    dz: Float32Array;
    nStations: number;
    nOffsets: number;
    offsetStartM: number;
    offsetStepM: number;
  };
  kind: StationVolumesAnalysisKind;
  /** Half-width of the neutral band, in metres (spec §3.2). */
  toleranceM: number;
  /** Half-width of the diverging ramp, in metres (spec §3.3). */
  spanM: number;
  /** The pane box to compose into. Defaults to `STRIP_PNG_PANE` for the
   *  Export menu, which can be used without the tab ever being opened and so
   *  has no measured box of its own. */
  paneWidth?: number;
  paneHeight?: number;
  t: (key: string, vars?: Record<string, unknown>) => string;
}

/** The pane box a strip PNG is composed at when the caller has none of its
 *  own — a landscape card wide enough for ~250 columns of chainage. */
export const STRIP_PNG_PANE = { width: 1200, height: 320 } as const;

/**
 * Build the `composeStripPng` input for a single-pair run. ONE place, so the
 * tab's own "Download PNG" and the Export menu's item cannot compose two
 * different pictures of the same run — the whole reason this module is pure
 * (see the header).
 *
 * The axis words go in BARE (`"L"` / `"R"`): `composeStripPng` folds the
 * swath widths into them itself, and pre-folding here would print them twice.
 */
export function stripPngInputFor(src: StripPngSource): StripPngInput {
  const { json, offsets, t } = src;
  return {
    json,
    matrix: { values: offsets.dz, nStations: offsets.nStations, nOffsets: offsets.nOffsets },
    toleranceM: src.toleranceM,
    spanM: src.spanM,
    offsetStartM: offsets.offsetStartM,
    offsetStepM: offsets.offsetStepM,
    kind: src.kind,
    swathLeftM: json.params.swathLeftM,
    swathRightM: json.params.swathRightM,
    referenceExtentLeftM: json.summary.referenceExtentLeftM ?? null,
    referenceExtentRightM: json.summary.referenceExtentRightM ?? null,
    paneWidth: src.paneWidth ?? STRIP_PNG_PANE.width,
    paneHeight: src.paneHeight ?? STRIP_PNG_PANE.height,
    title: `${t('stationVolumes.strip.title')} · ${json.alignment.name}`,
    // The live tab's own THREE pieces of caption, in the tab's own order: the
    // convention sentence (chosen on the same condition — with no neutral band
    // there is no "grey within ±x mm" clause to print, and printing one would
    // describe a picture that has no grey in it), the span the ramp saturates
    // at, and the orientation sentence. The tab shows all three under the
    // strip and the worker's PNG bakes the orientation into its own title, so
    // a browser export carrying only the first said less than either.
    legendText: [
      src.toleranceM > 0
        ? t('stationVolumes.strip.legend', { mm: Math.round(src.toleranceM * 1000) })
        : t('stationVolumes.strip.legendNoTolerance'),
      t('stationVolumes.strip.spanLabel', { span: formatFixed(src.spanM, 3) }),
      t('stationVolumes.strip.orientation'),
    ].join(' '),
    axisLeft: t('stationVolumes.strip.axisLeft'),
    axisCentre: t('stationVolumes.strip.axisCentre'),
    axisRight: t('stationVolumes.strip.axisRight'),
  };
}
